import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

export interface ConcreteCaseData {
  title: string;
  context: string;
  beforeMetric?: string | null;
  afterMetric?: string | null;
  metricLabel?: string | null;
  breakdown: Array<{ label: string; value: string; tone?: 'brand' | 'lavender' | 'sage' | 'accent' }>;
  highlight?: string | null;
  sourceLabel?: string | null;
}

export interface ConcreteCaseProps {
  eyebrow?: string;
  title?: string;
  description?: string;
  cases: ConcreteCaseData[];
  tone?: 'brand' | 'lavender' | 'sage' | 'lavender-700' | 'brand-700' | 'accent';
}

const TONE_PILL: Record<NonNullable<ConcreteCaseProps['tone']>, string> = {
  brand: 'bg-brand-50 text-brand-800 ring-brand-200/50',
  lavender: 'bg-lavender-50 text-lavender-800 ring-lavender-200/50',
  sage: 'bg-sage-100 text-sage-500 ring-sage-200/60',
  'lavender-700': 'bg-lavender-50 text-lavender-800 ring-lavender-300/50',
  'brand-700': 'bg-brand-50 text-brand-800 ring-brand-300/50',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/50',
};

const TONE_VALUE: Record<NonNullable<ConcreteCaseData['breakdown'][number]['tone']>, string> = {
  brand: 'text-brand-700',
  lavender: 'text-lavender-700',
  sage: 'text-sage-500',
  accent: 'text-accent-700',
};

export function ConcreteCase({
  eyebrow = 'Scénario type',
  title = 'Un scénario type, des chiffres concrets.',
  description,
  cases,
  tone = 'brand',
}: ConcreteCaseProps) {
  if (!cases || cases.length === 0) return null;

  return (
    <section className="bg-paper py-16 md:py-20 lg:py-24">
      <Container className="max-w-5xl">
        <Reveal>
          <div className="mb-10 max-w-2xl">
            <p className="eyebrow">{eyebrow}</p>
            <h2 className="mt-3 h-section">{title}</h2>
            {description && (
              <p className="mt-3 text-[15px] text-stone-600 leading-relaxed">{description}</p>
            )}
          </div>
        </Reveal>

        <div className="space-y-6">
          {cases.map((c, idx) => (
            <motion.article
              key={c.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45, delay: idx * 0.05, ease: easeStripe }}
              className="overflow-hidden rounded-2xl border border-stone-200/70 bg-paper shadow-[0_1px_2px_rgba(10,10,10,.04)]"
            >
              <div className="grid gap-0 lg:grid-cols-[1.2fr_1fr]">
                {/* Colonne gauche : context + breakdown */}
                <div className="border-b border-stone-200/70 p-6 lg:border-b-0 lg:border-r lg:p-8">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ring-1 ring-inset',
                      TONE_PILL[tone],
                    )}
                  >
                    <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />
                    Cas client signé
                  </span>

                  <h3 className="mt-3 font-display text-lg font-semibold leading-tight text-ink md:text-xl">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone-600">{c.context}</p>

                  {/* Breakdown lines */}
                  <ul className="mt-5 divide-y divide-stone-200/60 rounded-lg border border-stone-200/70 bg-stone-50/40">
                    {c.breakdown.map((b) => (
                      <li
                        key={b.label}
                        className="flex items-center justify-between px-3 py-2 text-sm"
                      >
                        <span className="text-stone-700">{b.label}</span>
                        <span
                          className={cn(
                            'font-mono text-sm font-semibold tabular-nums',
                            b.tone ? TONE_VALUE[b.tone] : 'text-ink',
                          )}
                        >
                          {b.value}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {c.highlight && (
                    <p className="mt-4 inline-flex items-start gap-2 text-xs text-stone-600">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" strokeWidth={2} />
                      <span>{c.highlight}</span>
                    </p>
                  )}
                </div>

                {/* Colonne droite : avant/après chiffres */}
                <div className="flex flex-col justify-center gap-5 bg-gradient-to-br from-stone-50/40 to-paper p-6 lg:p-8">
                  {c.metricLabel && (
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                      {c.metricLabel}
                    </p>
                  )}
                  <div className="flex items-end gap-5">
                    <div className="flex-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">
                        Avant
                      </p>
                      {c.beforeMetric && (
                        <p className="mt-1 font-mono text-xl font-semibold text-stone-400 line-through decoration-stone-300 tabular-nums">
                          {c.beforeMetric}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="mb-1 h-4 w-4 shrink-0 text-stone-400" strokeWidth={2} />
                    <div className="flex-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-700">
                        Avec SEF
                      </p>
                      {c.afterMetric && (
                        <p className="mt-1 metric-xl">{c.afterMetric}</p>
                      )}
                    </div>
                  </div>
                  {c.sourceLabel && (
                    <p className="mt-auto inline-flex items-center gap-1.5 border-t border-stone-200/60 pt-3 text-[11px] text-stone-500">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
                      {c.sourceLabel}
                    </p>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </Container>
    </section>
  );
}
